import React from 'react';
import { motion } from 'framer-motion';
export function HowItWorksSection() {
  const steps = [{
    illustration: <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2788">
          <rect x="20" y="15" width="60" height="70" rx="4" stroke="#FFD700" strokeWidth="2" strokeDasharray="4 4" className="animate-[spin_20s_linear_infinite]" data-id="element-2789" />
          <rect x="25" y="20" width="50" height="60" rx="2" fill="#0A0A0A" stroke="#374151" strokeWidth="2" data-id="element-2790" />
          <path d="M35 35H65M35 45H55M35 55H65M35 65H50" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" data-id="element-2791" />
          <path d="M50 80V50M50 50L40 60M50 50L60 60" stroke="#FF0000" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-2792" />
          <circle cx="80" cy="20" r="4" fill="#FF0000" className="animate-pulse" data-id="element-2793" />
        </svg>,
    title: 'Upload Conversations',
    description: 'Securely upload your chat screenshots or text exports. Your data is encrypted and private.'
  }, {
    illustration: <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2794">
          <circle cx="50" cy="50" r="35" stroke="#374151" strokeWidth="2" strokeDasharray="4 4" className="animate-[spin_15s_linear_infinite]" data-id="element-2795" />
          <circle cx="50" cy="50" r="25" fill="#0A0A0A" stroke="#FFD700" strokeWidth="2" data-id="element-2796" />
          <path d="M40 50C40 44.4772 44.4772 40 50 40C55.5228 40 60 44.4772 60 50C60 55.5228 55.5228 60 50 60C44.4772 60 40 55.5228 40 50Z" stroke="#FF0000" strokeWidth="2" data-id="element-2797" />
          <path d="M50 40V30M50 70V60M40 50H30M70 50H60M42.9289 42.9289L35.8579 35.8579M57.0711 57.0711L64.1421 64.1421M42.9289 57.0711L35.8579 64.1421M57.0711 42.9289L64.1421 35.8579" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" data-id="element-2798" />
          <rect x="48" y="20" width="4" height="60" fill="#FF0000" opacity="0.5" className="animate-[scan_2s_ease-in-out_infinite]" data-id="element-2799" />
        </svg>,
    title: 'AI Analysis',
    description: 'Our models analyze the communication for emotional patterns, tone, and hidden dynamics.'
  }, {
    illustration: <svg viewBox="0 0 100 100" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-2800">
          <rect x="15" y="20" width="70" height="60" rx="4" fill="#0A0A0A" stroke="#374151" strokeWidth="2" data-id="element-2801" />
          <rect x="15" y="20" width="70" height="15" fill="#1F2937" data-id="element-2802" />
          <circle cx="25" cy="27.5" r="3" fill="#FF0000" data-id="element-2803" />
          <circle cx="35" cy="27.5" r="3" fill="#FFD700" data-id="element-2804" />
          <circle cx="45" cy="27.5" r="3" fill="#10B981" data-id="element-2805" />
          <path d="M25 50L35 60L55 40" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-2806" />
          <path d="M65 45H75M65 55H75" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" data-id="element-2807" />
          <rect x="25" y="70" width="50" height="4" rx="2" fill="#374151" data-id="element-2808" />
          <rect x="25" y="70" width="35" height="4" rx="2" fill="#FFD700" data-id="element-2809" />
        </svg>,
    title: 'Receive Insights',
    description: 'Get a clear, objective report detailing communication styles, red flags, and healthy patterns.'
  }];
  return <section className="py-24 bg-[#050505] relative overflow-hidden border-t border-gray-900" data-id="element-2810">
      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-2811">
        <div className="text-center mb-16" data-id="element-2812">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight" data-id="element-2813">
            How it <span className="text-[#FF0000]" data-id="element-2814">works</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-mono" data-id="element-2815">
            Three simple steps to gain objective clarity on your digital
            interactions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2816">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          duration: 0.5
        }} className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-8 hover:border-[#FF0000]/50 transition-colors group relative overflow-hidden" data-id="element-2817">
              {/* Step Number Badge */}
              <div className="absolute top-4 right-4 font-mono text-[10px] font-bold text-gray-600 group-hover:text-[#FF0000] transition-colors" data-id="element-2818">
                STEP 0{index + 1}
              </div>

              {/* Illustration Container */}
              <div className="w-32 h-32 mx-auto mb-8 relative" data-id="element-2819">
                <div className="absolute inset-0 bg-[#FF0000]/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2820" />
                <div className="relative z-10 w-full h-full" data-id="element-2821">
                  {step.illustration}
                </div>
              </div>

              <h3 className="text-xl font-black text-white mb-3 uppercase tracking-wide text-center group-hover:text-[#FFD700] transition-colors" data-id="element-2822">
                {step.title}
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm text-center font-mono" data-id="element-2823">
                {step.description}
              </p>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-[#FF0000] group-hover:w-1/2 transition-all duration-300" data-id="element-2824" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}